import { Component, Input } from '@angular/core';

import {
    AllocationBalanceRow,
    BalanceSnapshot,
    balanceAmount,
    isBalanceAvailable,
    UNAVAILABLE_DISPLAY,
} from 'src/app/model/cashback-allocation-balance';

/**
 * LP-5358 / LP-5360: the supplier cashback allocation table, shared by the admin transaction
 * detail and the Enterprise transaction detail.
 *
 * <p><b>Why a component rather than copied markup.</b> LP-5360 needs this table in the Enterprise
 * view, and Angular component styles are scoped, so the Enterprise template could not even see the
 * admin component's CSS. Copying ~150 lines of financial-table markup is how two views of one
 * ledger drift into describing it differently -- different column names, one fixed and one not.
 * Both views now render the same component, so a presentation fix lands in both at once.
 *
 * <p><b>Presentational only.</b> No arithmetic, no inference, no backend calls. It receives
 * {@link AllocationBalanceRow}s already resolved at the LP-5352 contract boundary and renders them
 * verbatim. Row order is the API's order; nothing here sorts, merges, sums or de-duplicates legs
 * that share an execution or a transaction hash.
 *
 * <p><b>Both views are at parity.</b> An earlier revision put the Enterprise view into a
 * "balances withheld" mode, on the reading that its viewer is an enterprise counterparty. That was
 * wrong about the route: {@code /admin/enterprise/staff/transactions} is an internal Admin Portal
 * view for authorised administrators, so hiding fields there was role-based hiding justified only
 * by the word "enterprise" in the path. The mode is gone rather than left unused.
 *
 * <p><b>The distinction that matters.</b> Balances arrive as a discriminated union rather than
 * `number | null`, so an authoritative `0` renders as `0.00` while an absent snapshot renders as
 * the unavailable marker. The template branches on {@link hasBalance}, never on truthiness --
 * `Number('') === 0` is exactly the trap that would collapse the two.
 */
@Component({
    selector: 'app-cashback-allocation-table',
    templateUrl: './cashback-allocation-table.component.html',
    styleUrls: ['../styles/cashback-allocation-table.css'],
})
export class CashbackAllocationTableComponent {
    /** The legs to render, in the order the API sent them. */
    @Input() rows: AllocationBalanceRow[] = [];

    unavailableDisplay = UNAVAILABLE_DISPLAY;

    /**
     * Whether any leg carries an execution/batch reference.
     *
     * <p>Derived here rather than passed in, so the column's presence and its contents cannot
     * disagree. An entirely empty column is dropped instead of rendering a column of dashes.
     */
    get showExecutionColumn(): boolean {
        return this.rows.some((row) => row.executionRef !== null);
    }

    /**
     * True when the backend sent an authoritative value for this balance.
     *
     * <p>The template branches on this rather than on the amount, so an authoritative `0` renders
     * as `0.00` while an absent value renders as the unavailable marker.
     */
    hasBalance(snapshot: BalanceSnapshot): boolean {
        return isBalanceAvailable(snapshot);
    }

    /** The value for the `number` pipe; null whenever {@link hasBalance} is false. */
    balanceValue(snapshot: BalanceSnapshot): number | null {
        return balanceAmount(snapshot);
    }

    /**
     * A CSS modifier for the status pill.
     *
     * <p>Returns a class, not a colour decision on its own: the pill always shows the status TEXT
     * as well, because conveying an allocation outcome by colour alone fails anyone who cannot
     * distinguish them. Unrecognised statuses fall back to a neutral pill rather than being hidden
     * or relabelled -- the backend's status is authoritative and is displayed as sent.
     */
    statusClass(status: string): string {
        const normalised = (status || '').trim().toUpperCase();

        switch (normalised) {
            case 'SUCCESS':
                return 'allocation-status--success';
            case 'FAILED':
                return 'allocation-status--failed';
            case 'PENDING':
            case 'SUBMITTED':
                return 'allocation-status--pending';
            default:
                return 'allocation-status--neutral';
        }
    }
}
